import { useState } from 'react'
import { Button, DatePicker, Divider, Drawer, Input, Radio, Select } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import dayjs from 'dayjs'
import { useDivisions } from '../../../domain/divisions/divisionsQueries'
import { getCustomers } from '../../customers/customerApi'

const { RangePicker } = DatePicker

type CustomerOption = { label: string; value: number }

export type AllInvoicesFilterValues = {
  paidStatus: 'not-paid' | 'paid'
  search: string
  jobType?: number
  emailStatus?: 'sent' | 'not-sent'
  customerId?: number
  divisionId?: number
  qboStatus?: 'synced' | 'not-synced'
  creationDateStart?: string
  creationDateEnd?: string
  dueDateStart?: string
  dueDateEnd?: string
}

type Props = {
  filters: AllInvoicesFilterValues
  onChange: (filters: AllInvoicesFilterValues) => void
}

const jobTypeOptions = [
  { label: 'Project', value: 1 },
  { label: 'Service Ticket', value: 2 },
  { label: 'Maintenance', value: 3 },
]

const emailStatusOptions = [
  { label: 'Sent', value: 'sent' as const },
  { label: 'Not Sent', value: 'not-sent' as const },
]

const qboStatusOptions = [
  { label: 'Synced', value: 'synced' as const },
  { label: 'Not Synced', value: 'not-synced' as const },
]

const AllInvoicesFilters = ({ filters, onChange }: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([])
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false)

  const divisionsQuery = useDivisions()
  const divisionOptions = (divisionsQuery.data ?? []).map(d => ({ label: d.name, value: d.id }))

  const handleCustomerSearch = async (search: string) => {
    if (search.length < 2) return
    setCustomerSearchLoading(true)
    try {
      const customers = await getCustomers(search, 20, 1, false, true, false)
      setCustomerOptions(customers.map(c => ({ label: c.displayName, value: c.id })))
    } finally {
      setCustomerSearchLoading(false)
    }
  }

  const update = (partial: Partial<AllInvoicesFilterValues>) => {
    onChange({ ...filters, ...partial })
  }

  const additionalFiltersActive =
    filters.customerId != null ||
    filters.divisionId != null ||
    filters.qboStatus != null ||
    filters.creationDateStart != null ||
    filters.dueDateStart != null

  return <>
    <div style={{ display: 'flex', flex: 1, flexDirection: 'row', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <Radio.Group
        value={filters.paidStatus}
        onChange={(e) => update({ paidStatus: e.target.value })}
        optionType="button"
        buttonStyle="solid"
        options={[
          { label: 'Not Paid', value: 'not-paid' },
          { label: 'Paid', value: 'paid' },
        ]}
      />
      <Divider type="vertical" />
      <div style={{ flex: 1, minWidth: 200 }}>
        <Input.Search
          placeholder="Search by customer or job name"
          allowClear
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
        />
      </div>
      <Divider type="vertical" />
      <Select
        placeholder="Job Type"
        allowClear
        onChange={(value) => update({ jobType: value })}
        options={jobTypeOptions}
        style={{ minWidth: 140 }}
        value={filters.jobType}
      />
      <Select
        placeholder="Sent Status"
        allowClear
        onChange={(value) => update({ emailStatus: value })}
        options={emailStatusOptions}
        style={{ minWidth: 140 }}
        value={filters.emailStatus}
      />
      <Button
        icon={<FontAwesomeIcon icon={faFilter} />}
        onClick={() => setDrawerOpen(true)}
        type={additionalFiltersActive ? 'primary' : 'default'}
        ghost={additionalFiltersActive}
      >
        Additional Filters
      </Button>
    </div>

    <Drawer
      title="Additional Filters"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      placement="right"
      width={320}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <div style={{ marginBottom: 4 }}>Customer</div>
          <Select
            placeholder="Search for a customer"
            allowClear
            showSearch
            filterOption={false}
            onSearch={handleCustomerSearch}
            onChange={(value) => update({ customerId: value })}
            options={customerOptions}
            loading={customerSearchLoading}
            style={{ width: '100%' }}
            value={filters.customerId}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>Division</div>
          <Select
            placeholder="Select division"
            allowClear
            onChange={(value) => update({ divisionId: value })}
            options={divisionOptions}
            style={{ width: '100%' }}
            value={filters.divisionId}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>QuickBooks Status</div>
          <Select
            placeholder="Select status"
            allowClear
            onChange={(value) => update({ qboStatus: value })}
            options={qboStatusOptions}
            style={{ width: '100%' }}
            value={filters.qboStatus}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>Creation Date</div>
          <RangePicker
            placeholder={['From', 'To']}
            style={{ width: '100%' }}
            onChange={(dates) => update({
              creationDateStart: dates?.[0]?.toISOString(),
              creationDateEnd: dates?.[1]?.endOf('day').toISOString(),
            })}
            value={filters.creationDateStart && filters.creationDateEnd
              ? [dayjs(filters.creationDateStart), dayjs(filters.creationDateEnd)]
              : null
            }
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>Due Date</div>
          <RangePicker
            placeholder={['From', 'To']}
            style={{ width: '100%' }}
            onChange={(dates) => update({
              dueDateStart: dates?.[0]?.toISOString(),
              dueDateEnd: dates?.[1]?.endOf('day').toISOString(),
            })}
            value={filters.dueDateStart && filters.dueDateEnd
              ? [dayjs(filters.dueDateStart), dayjs(filters.dueDateEnd)]
              : null
            }
          />
        </div>
      </div>
    </Drawer>
  </>
}

export default AllInvoicesFilters
